import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { UserSettingStorageService } from './user-setting-storage.service';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ThemingService } from '@ngx-seatback/utils';
import { catchError, take } from 'rxjs/operators';
import { API } from './api/api';

@Injectable({
  providedIn: 'root',
})
export class UserSettingService {
  private _isInitialized = false;

  userSettings: WritableSignal<Record<string, string> | null> = signal<Record<string, string> | null>(null);
  selectedLanguage: Signal<string> = computed<string>(() => {
    const currentUserSettings = this.userSettings();
    let currentLanguage = this.resolveDefaultLang();
    if (!currentUserSettings) {
      // default language
      return currentLanguage;
    }
    const langFromUserSettings: string = currentUserSettings?.defaultLang;
    if (langFromUserSettings) {
      currentLanguage = langFromUserSettings;
    }
    return currentLanguage;
  });

  debugMode: Signal<boolean> = computed<boolean>(() => {
    const currentUserSettings = this.userSettings();
    if (!currentUserSettings) {
      // default Theming loaded
      return false;
    }
    const debugMode: string = currentUserSettings.debugMode?.toString();
    return debugMode && debugMode === 'true';
  });

  constructor(
    private readonly userSettingStorageService: UserSettingStorageService,
    private readonly translate: TranslateService,
    private readonly themingService: ThemingService,
    private readonly api: API
  ) {
    // language effect
    effect(() => {
      const currentLanguage = this.selectedLanguage();
      if (currentLanguage != this.translate.currentLang) {
        this.translate.use(currentLanguage).pipe(take(1)).subscribe();
        this.translate.currentLang = currentLanguage;
      }
    });

    // theming effect
    effect(() => {
      const currentUserSettings = this.userSettings();
      if (!currentUserSettings) {
        // default Theming loaded
        this.themingService.checkForDefaultSettings();
      }
      const themeFromUserSettings: string = currentUserSettings?.themeSetting;
      if (themeFromUserSettings && (themeFromUserSettings === 'dark-theme' || themeFromUserSettings === 'light-theme')) {
        this.themingService.changeTheme(themeFromUserSettings);
      }
    });
  }

  resolveDefaultLang(): string {
    let userLang = navigator.language.split('-')[0];
    userLang = /(de|en)/gi.test(userLang) ? userLang : 'en';
    return userLang;
  }

  updateUserSettings(data: Record<string, string> | null) {
    if (data) {
      this.userSettings.set({ ...data });
    } else {
      this.userSettings.set(null);
    }
    this.userSettingStorageService.updateSettings(data ?? null);
  }

  async getUserSettingsFromServer(): Promise<void> {
    const userSettings = await firstValueFrom(
      this.api.shared_User_retrieveSettings().pipe(
        take(1),
        catchError(err => {
          throw new Error(err);
        })
      )
    );
    this._isInitialized = true;
    this.updateUserSettings(userSettings);
  }

  async getUserSetting(key: string): Promise<string | null> {
    if (!this._isInitialized) {
      await this.getUserSettingsFromServer();
    }
    return Promise.resolve(this.userSettings()?.[key] ?? null);
  }

  async setUserSetting(key: string, value: string): Promise<void> {
    if (key == null || value == null) {
      throw new Error('Missing Key/Value pair definition');
    }
    try {
      const settings = await this.userSettingStorageService.store(key, value);
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_User_updateSettings(settings).pipe(
          take(1),
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateUserSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating user setting:', error);
      throw error;
    }
  }

  async clearUserSetting(key: string): Promise<void> {
    if (key == null) {
      throw new Error('Missing Key definition');
    }
    try {
      const settings = await this.userSettingStorageService.delete(key);
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_User_updateSettings(settings).pipe(
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateUserSettings(updatedSettings);
    } catch (error) {
      console.error('Error clearing user setting:', error);
      throw error;
    }
  }

  async getAllUserSettings(): Promise<Record<string, string>> {
    const currentSettings = await this.userSettingStorageService.fetchSettings();
    this.updateUserSettings(currentSettings);
    return currentSettings;
  }

  async clearAllUserSettings(): Promise<void> {
    this.userSettingStorageService.deleteSettings();
    const updatedSettings: Record<string, string> = await firstValueFrom(
      this.api.shared_User_clearSettings().pipe(
        catchError(err => {
          throw new Error(err);
        })
      )
    );
    this.updateUserSettings(updatedSettings);
  }
}
